import React from "react";

import { getActivitySessionPage, getSessionTimeline } from "../../lib/api";
import type { AuthHeaders } from "../../lib/auth";
import type { ActivitySession, DateRange } from "../../lib/types";
import {
  Callout,
  Card,
  EmptyState,
  Modal,
  Pill,
  RowButton,
  Segmented,
  Skeleton,
  StatStrip,
  StatTile,
  Table,
  Tooltip,
} from "../../design";
import {
  WORK,
  filtersOf,
  humanize,
  kindLabel,
  shortRepo,
  timeOf,
  toolLabel,
  usd,
  usdShort,
  useLoad,
  type ActiveFilter,
} from "./util";
import { failureOf } from "./failure";
import { findLoop } from "./loop";

function duration(a: string, b: string): string {
  const mins = Math.max(0, Math.round((Date.parse(b) - Date.parse(a)) / 60000));
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// The analytics API returns at most this many sessions per range.
const SESSION_CAP = 100;

/** "Latest 100 of 4.1K sessions" when the list is a sample of the range. */
export function sessionsTitle(shown: number, total: number | null): string {
  const noun = (n: number) => (n === 1 ? "session" : "sessions");
  if (total != null && total > shown)
    return `Latest ${humanize(shown)} of ${humanize(total)} ${noun(total)}`;
  if (total == null && shown >= SESSION_CAP)
    return `Latest ${humanize(shown)} ${noun(shown)}`;
  return `${humanize(shown)} ${noun(shown)} in this range`;
}

export function ActivityPanel({
  authHeaders,
  base,
  range,
  filters,
  showPeople = true,
  bare = false,
}: {
  authHeaders: AuthHeaders;
  base: string;
  range: DateRange;
  filters: ActiveFilter[];
  showPeople?: boolean;
  bare?: boolean;
}) {
  const flt = filtersOf(filters);
  const {
    data: page,
    error,
    loading,
  } = useLoad(
    () => getActivitySessionPage(authHeaders, base, range, flt),
    [authHeaders, base, range.start, range.end, JSON.stringify(flt)],
  );
  const [open, setOpen] = React.useState<ActivitySession | null>(null);
  const [sort, setSort] = React.useState<"recent" | "spend">("recent");

  if (error)
    return (
      <EmptyState icon="alert" title="Couldn't load sessions">
        {error}
      </EmptyState>
    );
  if (loading || !page)
    return (
      <div className="ana-stack" aria-busy>
        <Skeleton height={88} />
        <Skeleton height={240} />
      </div>
    );
  const data = page.sessions;
  const total = page.total ?? data.length;
  const sampled =
    page.total != null ? page.total > data.length : data.length >= SESSION_CAP;
  const among = sampled ? `the latest ${humanize(data.length)}` : null;
  const cost = data.reduce((s, x) => s + Number(x.cost_usd), 0);
  const failing = data.filter((s) => s.errors + s.tool_errors > 0).length;
  const repos = new Set(data.map((s) => s.repo).filter(Boolean)).size;

  return (
    <div className="ana-stack">
      {!bare && <h2 className="ds-section-title">Activity</h2>}
      <StatStrip>
        <StatTile
          label="Sessions"
          value={humanize(total)}
          note={
            sampled
              ? `Latest ${humanize(data.length)} listed below`
              : "In this range"
          }
        />
        <StatTile
          label="Spend at API prices"
          value={usdShort(cost)}
          note={among ? `Across ${among}` : undefined}
        />
        <StatTile
          label="Repositories"
          value={humanize(repos)}
          note={among ? `In ${among}` : undefined}
        />
        <StatTile
          label="Sessions with failures"
          value={humanize(failing)}
          note={
            data.length
              ? `${Math.round((failing / data.length) * 100)}% of ${among ?? "sessions"}`
              : undefined
          }
        />
      </StatStrip>
      <Card
        flush
        title={bare ? sessionsTitle(data.length, page.total) : "Sessions"}
        info="Each agent run started with the dense command. Its requests stay linked for 7 days."
        actions={
          <Segmented
            label="Sort sessions"
            toggle
            size="sm"
            value={sort}
            options={[
              { value: "recent", label: "Newest" },
              { value: "spend", label: "Most spend" },
            ]}
            onChange={setSort}
          />
        }
      >
        {data.length === 0 ? (
          <EmptyState compact title="No sessions in this range">
            Sessions come from the dense CLI.
          </EmptyState>
        ) : (
          <Table label="Sessions">
            <thead>
              <tr>
                {showPeople && <th>Person</th>}
                <th>Repository</th>
                <th className="num">Spend</th>
                <th>Started</th>
                <th className="num hide-lg">Requests</th>
                <th className="num">Failures</th>
                <th className="hide-lg">Work</th>
              </tr>
            </thead>
            <tbody>
              {[...data]
                .sort((a, z) =>
                  sort === "spend"
                    ? Number(z.cost_usd) - Number(a.cost_usd)
                    : Date.parse(z.started_at) - Date.parse(a.started_at),
                )
                .map((s) => {
                  const fails = s.errors + s.tool_errors;
                  return (
                    <RowButton
                      key={s.id}
                      label={`Open the timeline of ${s.repo ?? "this session"}`}
                      onOpen={() => setOpen(s)}
                    >
                      {showPeople && (
                        <td className="strong ana-nowrap">{s.email ?? "—"}</td>
                      )}
                      <td className="ana-ellipsis-cell ana-repocol">
                        <span className="strong">
                          {s.repo ? shortRepo(s.repo) : "No repo reported"}
                        </span>
                        <span className="ds-cell-sub ana-ellipsis">
                          {[s.branch, s.ticket, s.client ?? kindLabel(s.kind)]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </td>
                      <td className="num strong">{usdShort(s.cost_usd)}</td>
                      <td className="ana-nowrap">
                        {timeOf(s.started_at)}
                        <span className="ds-cell-sub">
                          {duration(s.started_at, s.last_at)}
                        </span>
                      </td>
                      <td className="num hide-lg">{humanize(s.requests)}</td>
                      <td className="num">
                        {fails > 0 ? (
                          <span
                            title={[
                              s.errors > 0 &&
                                `${s.errors} request${s.errors === 1 ? "" : "s"}`,
                              s.tool_errors > 0 &&
                                `${s.tool_errors} tool call${s.tool_errors === 1 ? "" : "s"}`,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          >
                            <Pill tone="bad" icon="alert">
                              {fails} failed
                            </Pill>
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="hide-lg">
                        <Pill>{WORK[s.work_type] ?? s.work_type}</Pill>
                      </td>
                    </RowButton>
                  );
                })}
            </tbody>
          </Table>
        )}
      </Card>
      {open && (
        <SessionTimeline
          authHeaders={authHeaders}
          base={base}
          session={open}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

export function SessionTimeline({
  authHeaders,
  base,
  session,
  onClose,
}: {
  authHeaders: AuthHeaders;
  base: string;
  session: ActivitySession;
  onClose: () => void;
}) {
  const { data, error, loading } = useLoad(
    () => getSessionTimeline(authHeaders, base, session.id),
    [authHeaders, base, session.id],
  );
  const loop = React.useMemo(() => (data ? findLoop(data) : null), [data]);
  const meta = [session.branch, session.ticket, session.models.join(", ")]
    .filter(Boolean)
    .join(" · ");
  return (
    <Modal
      eyebrow={session.email ?? "Session"}
      title={session.repo ? shortRepo(session.repo) : "Session timeline"}
      onClose={onClose}
      wide
    >
      <div className="ana-stack">
        {(meta || session.cost_basis === "api_equivalent") && (
          <div className="ana-meta-row">
            {meta && <span className="ds-meta">{meta}</span>}
            {session.cost_basis === "api_equivalent" && (
              <Pill tone="info" icon="info">
                Subscription: spend is the API-equivalent price
              </Pill>
            )}
          </div>
        )}
        {error ? (
          <EmptyState compact icon="alert" title="Couldn't load the timeline">
            {error}
          </EmptyState>
        ) : loading || !data ? (
          <Skeleton lines={6} />
        ) : (
          <>
            {loop && (
              <Callout tone="warn" role="note" title="Looks like a loop">
                {loop.length} turns in a row ran the same tools ({loop.tools})
                and cost {usd(loop.cost)}. An agent repeating itself usually
                needs a clearer next step or a stop.
              </Callout>
            )}
            <Table size="sm" label="Session timeline">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Model and tokens</th>
                  <th className="num">Spend</th>
                  <th className="num">Latency</th>
                  <th>Tools</th>
                </tr>
              </thead>
              <tbody>
                {data.map((r, i) => {
                  const failure = failureOf(r.status_code, r.error_class);
                  const inLoop =
                    loop != null &&
                    i >= loop.start &&
                    i < loop.start + loop.length;
                  return (
                    <tr
                      key={r.request_id}
                      className={inLoop ? "ana-loop" : undefined}
                    >
                      <td className="muted">{timeOf(r.at)}</td>
                      <td>
                        <span className="ana-inline">
                          {r.model ?? "—"}
                          {failure && (
                            <Tooltip
                              text={`${failure.detail} (${failure.code})`}
                            >
                              <Pill tone="bad" icon="x-circle">
                                {failure.label}
                              </Pill>
                            </Tooltip>
                          )}
                          {r.retry_of && (
                            <Tooltip text="The agent sent this again after an earlier request failed.">
                              <Pill>Retry</Pill>
                            </Tooltip>
                          )}
                        </span>
                        <span className="ds-cell-sub">
                          {humanize(
                            r.input_tokens + r.cache_read + r.cache_write,
                          )}{" "}
                          in ({humanize(r.cache_read)} cached) ·{" "}
                          {humanize(r.output_tokens)} out
                        </span>
                      </td>
                      <td className="num">
                        {r.cost_usd != null ? (
                          usd(r.cost_usd)
                        ) : (
                          <span className="muted">
                            {failure ? "Not billed" : "Settling"}
                          </span>
                        )}
                      </td>
                      <td className="num">
                        {r.latency_ms == null
                          ? "—"
                          : `${(r.latency_ms / 1000).toFixed(1)}s`}
                      </td>
                      <td>
                        <span className="ana-chips tight">
                          {r.tools.map((t) => (
                            <Pill
                              key={t.tool}
                              tone={
                                t.errors
                                  ? "bad"
                                  : t.mcp_server
                                    ? "info"
                                    : "neutral"
                              }
                              icon={t.errors ? "alert" : undefined}
                              title={t.tool}
                            >
                              {t.skill
                                ? `Skill: ${t.skill}`
                                : toolLabel(t.tool)}
                              {t.calls > 1 ? ` ${t.calls}x` : ""}
                            </Pill>
                          ))}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </div>
    </Modal>
  );
}
